import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  CalendarDays,
  ChartColumn,
  Flame,
  Loader2,
  Search,
  Send,
  Settings,
  ShoppingCart,
  Tag,
  TriangleAlert,
  Users,
  X
} from "lucide-react";
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { cn } from "@/lib/utils";
import { Alert, AlertAction, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { VendorState } from '../../../redux/vendor/rootReducer';
import { Event } from "./timeline-types";
import { EmailTemplate, EmailTemplateCustomization } from "./timeline-types";
import { EmailTemplatesService } from "../services/email-templates.service";
import { useEmailApi, SendEmailRequest } from "../../../common/hooks/use-email-api.hook";
import { useSettingsApi } from "../../../common/hooks/use-settings-api.hook";
import { useAnalyticsApi } from "../../../common/hooks/use-analytics-api.hook";
import { useTranslation, Trans } from 'react-i18next';
import { Lead } from "../../../common/hooks/use-leads-api.hook";
import EmailTrackingAnalytics from './email-tracking-analytics.component';
import Lottie from 'lottie-react';
import sendMessageAnimation from '../../../assets/send-message-lottie.json';

interface ProductAnalysis {
  productName: string;
  productBrand?: string;
  thumbnailPath?: string;
  productSku?: string;
  productId?: string;
  usageCount: number;
  visitCount: number;
  totalInteractions: number;
  lastInteraction: string;
  eventTypes: string[];
}

interface MarketingEmailComposerProps {
  onClose: () => void;
  leadEmail: string;
  leadEvents: Event[];
  leadData?: Lead;
  allLeads?: Lead[];
  onLeadSelect?: (email: string) => void;
  isLoadingLead?: boolean;
}

/** Small AI sparkle glyph kept from the original design (no lucide equivalent). */
const SparkleIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 3 C 10 8, 13 11, 18 12 C 13 13, 10 16, 10 21 C 10 16, 7 13, 2 12 C 7 11, 10 8, 10 3 Z" />
    <path d="M18 2 C 18 4, 19 5, 21 5 C 19 5, 18 6, 18 8 C 18 6, 17 5, 15 5 C 17 5, 18 4, 18 2 Z" />
    <path d="M19 15 C 19 17, 20 18, 22 18 C 20 18, 19 19, 19 21 C 19 19, 18 18, 16 18 C 18 18, 19 17, 19 15 Z" />
  </svg>
);

/** One label/value line in the campaign summary — the dialog lists five of them. */
const SummaryRow = ({ label, children }: { label: React.ReactNode; children: React.ReactNode }) => (
  <div className="flex gap-4">
    <span className="min-w-20 text-xs font-medium text-muted-foreground">{label}</span>
    <span className="flex-1 text-xs text-foreground">{children}</span>
  </div>
);

export const MarketingEmailComposer = ({
  onClose,
  leadEmail,
  leadEvents,
  leadData,
  allLeads = [],
  onLeadSelect,
  isLoadingLead = false
}: MarketingEmailComposerProps) => {
  const { t } = useTranslation();
  // Restore draft from localStorage
  const savedDraft = useMemo(() => {
    try {
      const raw = localStorage.getItem('ardealy.email.draft');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }, []);

  const [selectedProducts, setSelectedProducts] = useState<string[]>(savedDraft?.selectedProducts || []);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [subject, setSubject] = useState(savedDraft?.subject || "");
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [userProductsData, setUserProductsData] = useState<any[]>([]);
  const [emailSentSuccess, setEmailSentSuccess] = useState(false);
  // Why the last send failed. Until now failures were only console.error'd, so a
  // rejected send looked identical to nothing happening.
  const [sendError, setSendError] = useState<string | null>(null);
  const [templateAnchorEl, setTemplateAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [layoutAnchorEl, setLayoutAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [voucherDropdownOpen, setVoucherDropdownOpen] = useState(false);
  const [selectedProductLayout, setSelectedProductLayout] = useState(savedDraft?.selectedProductLayout || 'minimal-list');

  // New state for tracking analytics
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const [trackingId, setTrackingId] = useState<string | null>(null);

  // Voucher state
  const [voucherAnchorEl, setVoucherAnchorEl] = useState<HTMLButtonElement | null>(null);

  // Lead selector state
  const [leadSelectorAnchor, setLeadSelectorAnchor] = useState<HTMLElement | null>(null);
  const [leadSearchTerm, setLeadSearchTerm] = useState('');

  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [aiGuideStep, setAiGuideStep] = useState<'select-lead' | 'select-products' | null>(null);

  const { sendEmail, generateEmailCopy, testSmtpConnection } = useEmailApi();
  const settingsApi = useSettingsApi();
  const navigate = useNavigate();
  const [smtpError, setSmtpError] = useState(false);
  const [isCheckingSmtp, setIsCheckingSmtp] = useState(false);
  // The compliance footer (unsubscribe link + postal address) is added server-side on
  // send, so it never appears in the preview iframe. Surface it here instead, and warn
  // when there is no address — the API refuses the send without one.
  const [postalAddress, setPostalAddress] = useState<string | null>(null);
  const [postalAddressStatus, setPostalAddressStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  /**
   * True only when we positively know no address is configured — this disables the send
   * button. A failed config lookup deliberately does NOT count: it must not strand the
   * vendor, and the server guards the send regardless.
   */
  const missingPostalAddress = postalAddressStatus === 'ready' && !postalAddress;
  const analyticsAPI = useAnalyticsApi();
  const { theme: vendorTheme } = useSelector((state: VendorState) => state.settings);

  // Get current language from vendor theme or default to 'en'
  const currentLanguage = vendorTheme?.language || 'en';

  const defaultCustomization: EmailTemplateCustomization = {
    companyName: vendorTheme?.vendorName || 'Your Company',
    companyLogo: vendorTheme?.logo || '',
    headerTitle: t('MARKETING_EMAIL.DEFAULT_HEADER_TITLE'),
    headerSubtitle: t('MARKETING_EMAIL.DEFAULT_HEADER_SUBTITLE'),
    footerText: t('MARKETING_EMAIL.DEFAULT_FOOTER_TEXT'),
    primaryColor: vendorTheme?.primaryColor || '#667eea',
    secondaryColor: vendorTheme?.secondaryColor || '#764ba2',
    includeVoucher: false,
    voucherCode: '',
    voucherValue: '10%',
    voucherExpiryDate: dayjs().add(30, 'day').toISOString(),
    language: currentLanguage,
    welcomeMessage: t('MARKETING_EMAIL.DEFAULT_WELCOME_MESSAGE'),
    productSectionTitle: t('MARKETING_EMAIL.DEFAULT_PRODUCT_SECTION_TITLE'),
    voucherTitle: t('MARKETING_EMAIL.DEFAULT_VOUCHER_TITLE'),
    voucherDescription: t('MARKETING_EMAIL.DEFAULT_VOUCHER_DESCRIPTION'),
    ctaButtonText: t('MARKETING_EMAIL.DEFAULT_CTA_BUTTON_TEXT'),
    copyrightText: t('MARKETING_EMAIL.ALL_RIGHTS_RESERVED'),
    poweredByText: 'Powered by',
    poweredByArdealyText: t('MARKETING_EMAIL.POWERED_BY_ARDEALY')
  };

  const [customization, setCustomization] = useState<EmailTemplateCustomization>(
    savedDraft?.customization ? { ...defaultCustomization, ...savedDraft.customization } : defaultCustomization
  );

  // Update customization when vendor theme changes
  useEffect(() => {
    if (vendorTheme) {
      setCustomization(prev => ({
        ...prev,
        companyName: vendorTheme.vendorName || prev.companyName,
        companyLogo: vendorTheme.logo || prev.companyLogo,
        primaryColor: vendorTheme.primaryColor || prev.primaryColor,
        secondaryColor: vendorTheme.secondaryColor || prev.secondaryColor,
        language: vendorTheme.language || prev.language
      }));
    }
  }, [vendorTheme]);

  // Listen for edit requests from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'updateField') {
        setCustomization(prev => ({
          ...prev,
          [event.data.field]: event.data.value
        }));
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Auto-enable/disable voucher based on required fields
  useEffect(() => {
    const hasRequiredVoucherFields = Boolean(customization.voucherCode.trim() && customization.voucherValue.trim());
    if (customization.includeVoucher !== hasRequiredVoucherFields) {
      setCustomization(prev => ({
        ...prev,
        includeVoucher: hasRequiredVoucherFields
      }));
    }
  }, [customization.voucherCode, customization.voucherValue, customization.includeVoucher]);

  // Get products from lead data instead of making API calls
  const getLeadProducts = useCallback(() => {
    if (!leadData?.products) {
      console.log('No lead data or products available');
      setUserProductsData([]);
      return;
    }

    console.log('Using products from lead data:', leadData.products);

    // Transform lead products to match the expected format and ensure uniqueness
    const transformedProducts = leadData.products.map(product => ({
      productName: product.title,
      productId: product.productId,
      thumbnailPath: product.thumbnailPath,
      addToCartLink: product.addToCartLink, // Prioritize add to cart link
      link: product.link, // Keep original link property
      productLink: product.link, // Also keep as productLink for backward compatibility
      usageCount: product.usageCount,
      visitCount: 0, // Not available in lead data
      totalInteractions: product.usageCount,
      lastInteraction: product.lastUsedAt,
      eventTypes: ['ProductUsageEvent'], // Assume usage events
      productBrand: '', // Not available in lead data
      productSku: product.productId // Use productId as SKU
    }));

    // Deduplicate products by productId and productName combination
    const uniqueProducts = transformedProducts.reduce((acc, current) => {
      const uniqueKey = `${current.productId}-${current.productName}`;
      const existing = acc.find(item => `${item.productId}-${item.productName}` === uniqueKey);

      if (!existing) {
        acc.push(current);
      } else {
        // If duplicate found, merge usage counts and keep the most recent interaction
        existing.usageCount += current.usageCount;
        existing.totalInteractions += current.totalInteractions;
        if (new Date(current.lastInteraction) > new Date(existing.lastInteraction)) {
          existing.lastInteraction = current.lastInteraction;
        }
      }

      return acc;
    }, [] as typeof transformedProducts);

    console.log(`Processed ${transformedProducts.length} products, ${uniqueProducts.length} unique products after deduplication`);
    setUserProductsData(uniqueProducts);
  }, [leadData]);

  // Load user product usage when component mounts or leadData changes
  useEffect(() => {
    if (leadData) {
      getLeadProducts();
    }
  }, [leadData, getLeadProducts]);

  // Analyze products from lead data directly
  const productAnalysis = useMemo(() => {
    // Use the transformed products from userProductsData (which comes from lead data)
    if (userProductsData && Array.isArray(userProductsData)) {
      return userProductsData
        .filter(product => product.totalInteractions > 0)
        .sort((a, b) => b.totalInteractions - a.totalInteractions);
    }

    return [];
  }, [userProductsData]);

  // Save draft to localStorage
  useEffect(() => {
    const draft = {
      subject,
      selectedProducts,
      selectedProductLayout,
      leadEmail: leadEmail || null,
      templateId: selectedTemplate?.id || null,
      customization: {
        headerTitle: customization.headerTitle,
        headerSubtitle: customization.headerSubtitle,
        welcomeMessage: customization.welcomeMessage,
        productSectionTitle: customization.productSectionTitle,
        ctaButtonText: customization.ctaButtonText,
        footerText: customization.footerText,
        includeVoucher: customization.includeVoucher,
        voucherCode: customization.voucherCode,
        voucherValue: customization.voucherValue,
        voucherExpiryDate: customization.voucherExpiryDate,
      },
    };
    localStorage.setItem('ardealy.email.draft', JSON.stringify(draft));
  }, [subject, selectedProducts, selectedProductLayout, leadEmail, selectedTemplate, customization]);

  // Restore lead from draft on mount
  useEffect(() => {
    if (savedDraft?.leadEmail && onLeadSelect && !leadEmail) {
      onLeadSelect(savedDraft.leadEmail);
    }
  }, []);

  // Load templates on component mount
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        console.log('Loading email templates...');
        const availableTemplates = await EmailTemplatesService.getAllTemplates();
        console.log('Available templates:', availableTemplates);
        setTemplates(availableTemplates);
        if (availableTemplates.length > 0) {
          // Restore saved template or use first
          const saved = savedDraft?.templateId
            ? availableTemplates.find(t => t.id === savedDraft.templateId)
            : null;
          setSelectedTemplate(saved || availableTemplates[0]);
        }
      } catch (error) {
        console.error('Error loading templates:', error);
      }
    };

    loadTemplates();
  }, []);

  const handleProductToggle = (productId: string | undefined, productName: string) => {
    // Create a safe unique key - use productName as fallback if productId is missing
    const safeProductId = productId || productName;
    const uniqueKey = `${safeProductId}-${productName}`;

    console.log('Toggling product:', { productId, productName, uniqueKey });

    setSelectedProducts(prev =>
      prev.includes(uniqueKey)
        ? prev.filter(p => p !== uniqueKey)
        : [...prev, uniqueKey]
    );
  };

  const hasDraftContent = !!(subject || leadEmail || selectedProducts.length > 0);

  const handleClearAll = () => {
    setSubject('');
    setSelectedProducts([]);
    setUserProductsData([]);
    setSelectedProductLayout('minimal-list');
    setCustomization(defaultCustomization);
    setAiGuideStep(null);
    if (onLeadSelect) onLeadSelect('');
    localStorage.removeItem('ardealy.email.draft');
  };

  const handleGenerateWithAI = async () => {
    // Step 1: Check lead
    if (!leadEmail) {
      setAiGuideStep('select-lead');
      // Open lead selector popover using the lead selector box as anchor
      const leadBox = document.getElementById('lead-selector-box');
      if (leadBox) {
        setLeadSelectorAnchor(leadBox);
      }
      return;
    }

    // Step 2: Check products
    if (selectedProducts.length === 0) {
      setAiGuideStep('select-products');
      return;
    }

    // Step 3: All set — generate
    setAiGuideStep(null);
    setIsGeneratingAI(true);
    try {
      const selectedProductData = productAnalysis.filter(p => {
        const key = `${p.productId || p.productName}-${p.productName}`;
        return selectedProducts.includes(key);
      });

      const result = await generateEmailCopy({
        products: selectedProductData.map(p => ({
          name: p.productName,
          category: p.category,
          usageCount: p.usageCount || p.totalInteractions,
        })),
        leadEmail,
        companyName: vendorTheme?.vendorName || 'Our Company',
        language: currentLanguage,
      });

      if (result.success && result.data) {
        setSubject(result.data.subject);
        setCustomization(prev => ({
          ...prev,
          headerTitle: result.data.headerTitle,
          headerSubtitle: result.data.headerSubtitle,
          welcomeMessage: result.data.welcomeMessage,
          productSectionTitle: result.data.productSectionTitle,
          ctaButtonText: result.data.ctaButtonText,
          footerText: result.data.footerText,
        }));
      }
    } catch (error) {
      console.error('Failed to generate email copy:', error);
    } finally {
      setIsGeneratingAI(false);
    }
  };

  // Advance AI guide when conditions are met
  useEffect(() => {
    if (aiGuideStep === 'select-lead' && leadEmail) {
      // Lead was selected — move to products check
      if (selectedProducts.length === 0) {
        setAiGuideStep('select-products');
      } else {
        setAiGuideStep(null);
      }
    }
    if (aiGuideStep === 'select-products' && selectedProducts.length > 0) {
      setAiGuideStep(null);
    }
  }, [leadEmail, selectedProducts, aiGuideStep]);

  const handleSendEmail = async () => {
    if (!selectedTemplate || selectedProducts.length === 0 || !subject.trim()) {
      console.log('Cannot send email: missing required fields');
      return;
    }

    console.log('Sending email to:', leadEmail);
    console.log('Selected products:', selectedProducts);
    console.log('Selected layout:', selectedProductLayout);
    console.log('Email subject:', subject);

    setIsSendingEmail(true);
    setSendError(null);
    try {
      const filteredProducts = getFilteredProductsForLayout();

      console.log('Filtered products for layout:', filteredProducts);

      const htmlContent = EmailTemplatesService.replaceTemplatePlaceholders(
        selectedTemplate,
        customization,
        filteredProducts,
        customization.includeVoucher,
        selectedProductLayout
      );

      console.log('Generated HTML content length:', htmlContent.length);

      // Build product URL to product ID mapping
      const productUrlMap: Record<string, string> = {};
      filteredProducts.forEach(product => {
        if (product.productId) {
          // Map all possible URLs for this product
          if (product.link) {
            productUrlMap[product.link] = product.productId;
          }
          if (product.addToCartLink && product.addToCartLink !== product.link) {
            productUrlMap[product.addToCartLink] = product.productId;
          }
          if (product.productLink && product.productLink !== product.link && product.productLink !== product.addToCartLink) {
            productUrlMap[product.productLink] = product.productId;
          }
        }
      });

      const emailRequest: SendEmailRequest = {
        email: leadEmail,
        subject: subject,
        htmlPayload: htmlContent,
        productIds: filteredProducts.map(p => p.productId).filter(Boolean),
        fromName: customization.companyName,
        voucherCode: customization.includeVoucher ? customization.voucherCode : undefined,
        templateId: selectedTemplate?.id || selectedTemplate?.name,
        productLayout: selectedProductLayout,
        productUrlMap: productUrlMap
      };

      // Get visitor ID from lead data (use first visitor ID if available)
      const visitorId = leadData?.visitorIds?.[0];

      console.log('Sending email request:', emailRequest);
      console.log('Using visitor ID:', visitorId);
      const response = await sendEmail(emailRequest, visitorId);
      console.log('Email API response:', response);

      if (response.success) {
        console.log('Email sent successfully');
        // Store tracking ID if available
        if (response.trackingId) {
          setTrackingId(response.trackingId);
          console.log('Email tracking ID:', response.trackingId);
        }
        setConfirmSendOpen(false);
        setEmailSentSuccess(true);
        localStorage.removeItem('ardealy.email.draft');
      } else {
        console.error('Email sending failed:', response.message);
        // Surface it — a blocked send (no postal address, quota reached) is
        // actionable and must not look like the button did nothing.
        setSendError(response.message || t('MARKETING_EMAIL.SEND_FAILED'));
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setSendError(t('MARKETING_EMAIL.SEND_FAILED'));
    } finally {
      setIsSendingEmail(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const config = await settingsApi.findConfig();
        if (cancelled) return;
        setPostalAddress(config?.smtp?.marketingPostalAddress?.trim() || null);
        setPostalAddressStatus('ready');
      } catch {
        // Couldn't find out. Say nothing and block nothing — the send is guarded
        // server-side anyway, so a transient config failure must not strand the vendor.
        if (!cancelled) setPostalAddressStatus('error');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // MUI Snackbar auto-hid the SMTP toast; the plain toast needs its own timer.
  useEffect(() => {
    if (!smtpError) return;
    const timer = window.setTimeout(() => setSmtpError(false), 2500);
    return () => window.clearTimeout(timer);
  }, [smtpError]);

  const handleVoucherClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setVoucherAnchorEl(event.currentTarget);
  };

  const handleVoucherClose = () => {
    setVoucherAnchorEl(null);
  };

  const handleTemplateSelect = (template: EmailTemplate) => {
    setSelectedTemplate(template);
  };

  const voucherOpen = Boolean(voucherAnchorEl);
  const isVoucherEnabled = Boolean(customization.voucherCode.trim() && customization.voucherValue.trim());

  // Get available product layouts
  const productLayouts = EmailTemplatesService.getProductLayouts();

  // Helper function to translate layout names
  const getLayoutName = (layoutId: string) => {
    switch (layoutId) {
      case 'minimal-list':
        return t('MARKETING_EMAIL.PRODUCT_LAYOUT.MINIMAL_LIST');
      case 'grid-cards':
        return t('MARKETING_EMAIL.PRODUCT_LAYOUT.GRID_CARDS');
      case 'horizontal-list':
        return t('MARKETING_EMAIL.PRODUCT_LAYOUT.HORIZONTAL_LIST');
      case 'featured-hero':
        return t('MARKETING_EMAIL.PRODUCT_LAYOUT.FEATURED_HERO');
      case 'compact-tiles':
        return t('MARKETING_EMAIL.PRODUCT_LAYOUT.COMPACT_TILES');
      case 'magazine-style':
        return t('MARKETING_EMAIL.PRODUCT_LAYOUT.MAGAZINE_STYLE');
      default:
        return layoutId;
    }
  };

  // Filter leads based on search term and sort by most recent
  const filteredLeads = useMemo(() => {
    let leads = allLeads;

    // Filter by search term if provided
    if (leadSearchTerm.trim()) {
      const search = leadSearchTerm.toLowerCase();
      leads = leads.filter(lead => lead.email.toLowerCase().includes(search));
    }

    // Sort by most recent first (assuming higher eventCount or createdAt means more recent activity)
    // If leads have a lastInteraction or createdAt field, use that; otherwise sort by eventCount
    return [...leads].sort((a, b) => {
      // Sort by eventCount as a proxy for recent activity (higher count = more recent)
      return (b.eventCount || 0) - (a.eventCount || 0);
    });
  }, [allLeads, leadSearchTerm]);

  // Handle lead selector
  const handleLeadSelectorOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLeadSelectorAnchor(event.currentTarget);
  };

  const handleLeadSelectorClose = () => {
    setLeadSelectorAnchor(null);
    setLeadSearchTerm('');
  };

  const handleLeadSelectFromPopover = (email: string) => {
    if (onLeadSelect) {
      onLeadSelect(email);
    }
    handleLeadSelectorClose();
  };

  // Filter products based on layout limits
  const getFilteredProductsForLayout = useCallback(() => {
    const layout = productLayouts.find(l => l.id === selectedProductLayout);
    const maxProducts = layout?.maxProducts || 6;

    const selectedProductsData = productAnalysis.filter(p => {
      // Use the same safe key generation as in handleProductToggle
      const safeProductId = p.productId || p.productName;
      const uniqueKey = `${safeProductId}-${p.productName}`;
      return selectedProducts.includes(uniqueKey);
    });

    return selectedProductsData.slice(0, maxProducts);
  }, [productAnalysis, selectedProducts, selectedProductLayout, productLayouts]);

  const generateEmailPreview = () => {
    if (!selectedTemplate) return '';

    const filteredProducts = getFilteredProductsForLayout();

    const htmlContent = EmailTemplatesService.replaceTemplatePlaceholders(
      selectedTemplate,
      customization,
      filteredProducts,
      customization.includeVoucher,
      selectedProductLayout
    );

    // Add CSS to disable all links in preview
    const styleTag = `
      <style>
        a {
          pointer-events: none !important;
          cursor: default !important;
        }
      </style>
    `;

    // Insert the style tag before closing head or at the beginning of body
    if (htmlContent.includes('</head>')) {
      return htmlContent.replace('</head>', `${styleTag}</head>`);
    } else if (htmlContent.includes('<body')) {
      return htmlContent.replace('<body', `${styleTag}<body`);
    } else {
      return styleTag + htmlContent;
    }
  };

  const settingsBarButtonClass = "h-8";

  return (
    <>
      <div className="flex h-full flex-col overflow-hidden bg-background">
        {/* Main Content Area */}
        <div className="flex flex-1 flex-col overflow-hidden bg-background md:flex-row">
          {/* Left Panel - Controls */}
          <div className="flex w-full max-h-[50vh] shrink-0 flex-col overflow-auto border-b bg-background md:h-full md:max-h-none md:w-[380px] md:border-b-0 md:border-e">
            {/* Email Subject and Lead Selector */}
            <div className="mb-1 flex shrink-0 flex-col gap-3 border-b px-3 pt-2 pb-4">
              {/* Email Subject */}
              <Input
                placeholder={t('MARKETING_EMAIL.EMAIL_SUBJECT') as string}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />

              {/* AI Guide Message */}
              {aiGuideStep === 'select-lead' && (
                <p className="animate-in fade-in text-xs font-medium text-foreground">
                  {t('MARKETING_EMAIL.AI_GUIDE_SELECT_LEAD')}
                </p>
              )}

              {/* Lead Selector Button with Analytics */}
              {allLeads.length > 0 && onLeadSelect && (
                <div className="flex items-center gap-2">
                  <Popover
                    open={Boolean(leadSelectorAnchor)}
                    onOpenChange={(open) => {
                      if (!open) handleLeadSelectorClose();
                    }}
                  >
                    <PopoverTrigger asChild>
                      <button
                        id="lead-selector-box"
                        type="button"
                        disabled={isLoadingLead}
                        onClick={handleLeadSelectorOpen}
                        className={cn(
                          "flex h-9 min-w-0 flex-1 items-center gap-2 border bg-background px-3 text-start transition-colors",
                          isLoadingLead ? "cursor-not-allowed opacity-60" : "hover:bg-muted",
                          aiGuideStep === 'select-lead' && "border-ring ring-2 ring-ring/30"
                        )}
                      >
                        {leadEmail ? (
                          <>
                            <Avatar size="sm">
                              <AvatarFallback className="font-medium">
                                {leadEmail.charAt(0).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                              {leadEmail}
                            </span>
                          </>
                        ) : (
                          <>
                            <Users className="size-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              {t('TIMELINE.SELECT_LEAD')}
                            </span>
                          </>
                        )}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-[360px] p-4">
                      {/* Header */}
                      <div className="mb-3">
                        <p className="text-sm font-medium text-foreground">{t('TIMELINE.PROSPECTS')}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {allLeads.length} {t('TIMELINE.LEADS_AVAILABLE')}
                        </p>
                      </div>

                      {/* Search Bar */}
                      <div className="relative mb-3">
                        <Search className="absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          className="h-9 ps-8"
                          placeholder={t('TIMELINE.SEARCH_PLACEHOLDER') as string}
                          value={leadSearchTerm}
                          onChange={(e) => setLeadSearchTerm(e.target.value)}
                        />
                      </div>

                      {/* Individual Prospects */}
                      <div className="max-h-80 overflow-auto pe-1">
                        {filteredLeads.length === 0 ? (
                          <div className="flex flex-col items-center justify-center px-4 py-8">
                            <span className="mb-3 flex size-12 items-center justify-center rounded-full bg-muted">
                              <Users className="size-6 text-muted-foreground" />
                            </span>
                            <p className="mb-1 text-center text-sm font-medium text-foreground">
                              {leadSearchTerm ? 'No matching leads' : t('TIMELINE.NO_PROSPECTS')}
                            </p>
                            <p className="max-w-60 text-center text-xs text-muted-foreground">
                              {leadSearchTerm ? 'Try a different search term' : t('TIMELINE.NO_PROSPECTS_MESSAGE')}
                            </p>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            {filteredLeads.map((lead) => (
                              <button
                                key={lead.email}
                                type="button"
                                onClick={() => handleLeadSelectFromPopover(lead.email)}
                                className={cn(
                                  "flex w-full items-center gap-3 border px-3 py-2 text-start transition-colors",
                                  leadEmail === lead.email
                                    ? "border-foreground bg-muted"
                                    : "border-transparent hover:border-border hover:bg-muted"
                                )}
                              >
                                {/* Avatar */}
                                <Avatar className="size-9 shrink-0">
                                  <AvatarFallback className="font-medium">
                                    {lead.email.charAt(0).toUpperCase()}
                                  </AvatarFallback>
                                </Avatar>

                                {/* Lead Info */}
                                <span className="min-w-0 flex-1">
                                  <span className="block truncate text-sm font-medium text-foreground">
                                    {lead.email}
                                  </span>
                                  <span className="block text-xs text-muted-foreground">
                                    {lead.eventCount} {t('MARKETING_EMAIL.EVENTS')}
                                  </span>
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>

                  {/* Analytics Toggle */}
                  {leadEmail && (
                    <Button
                      variant="outline"
                      size="icon-lg"
                      aria-pressed={analyticsDialogOpen}
                      aria-label={t('EMAIL_TRACKING.RECENT_ACTIVITY') as string}
                      className={cn(analyticsDialogOpen && "border-foreground bg-muted")}
                      onClick={() => setAnalyticsDialogOpen(!analyticsDialogOpen)}
                    >
                      {analyticsDialogOpen ? <X /> : <ChartColumn />}
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Inline Analytics Panel */}
            {analyticsDialogOpen && leadEmail && (
              <div className="animate-in fade-in max-h-[300px] overflow-auto border-b">
                <EmailTrackingAnalytics
                  email={leadEmail}
                  onRefresh={() => {
                    console.log('Analytics refreshed for:', leadEmail);
                  }}
                />
              </div>
            )}

            {/* Product Selection */}
            <div
              className={cn(
                "border-b px-4 py-3 transition-colors",
                aiGuideStep === 'select-products' && "border-s-2 border-s-ring bg-muted/50"
              )}
            >
              {aiGuideStep === 'select-products' && (
                <p className="animate-in fade-in mb-1.5 text-xs font-medium text-foreground">
                  {t('MARKETING_EMAIL.AI_GUIDE_SELECT_PRODUCTS')}
                </p>
              )}
              <p className="text-sm font-medium text-foreground">
                {t('MARKETING_EMAIL.PRODUCT_SELECTION')}
              </p>
              <span className="text-xs text-muted-foreground">
                {selectedProducts.length} {t('MARKETING_EMAIL.SELECTED')}
              </span>
            </div>

            <div className="flex-1 overflow-x-auto overflow-y-hidden p-3 md:overflow-x-hidden md:overflow-y-auto">
              {productAnalysis.length === 0 ? (
                <p className="block px-4 py-4 text-center text-xs text-muted-foreground">
                  {t('MARKETING_EMAIL.NO_PRODUCT_DATA_FOUND')}
                </p>
              ) : (
                <div className="flex min-w-max flex-row gap-2 md:min-w-0 md:flex-col">
                  {productAnalysis.map((product, index) => {
                    const safeProductId = product.productId || product.productName;
                    const uniqueKey = `${safeProductId}-${product.productName}`;
                    const isSelected = selectedProducts.includes(uniqueKey);
                    const isHotProduct = index === 0;

                    return (
                      <div
                        key={uniqueKey}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleProductToggle(product.productId, product.productName)}
                        className={cn(
                          "flex min-w-[220px] cursor-pointer items-center gap-3 border p-2 transition-colors md:min-w-0",
                          isSelected
                            ? "border-foreground bg-muted"
                            : "border-border hover:border-input hover:bg-muted"
                        )}
                      >
                        {/* Checkbox + Fire icon */}
                        <div className="-me-1 flex shrink-0 items-center gap-1">
                          <Checkbox checked={isSelected} className="pointer-events-none" />
                          {isHotProduct && <Flame className="size-4 text-warning" />}
                        </div>

                        {/* Thumbnail */}
                        <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden border bg-muted">
                          {product.thumbnailPath ? (
                            <img
                              src={product.thumbnailPath}
                              alt={product.productName}
                              className="size-full object-cover"
                            />
                          ) : (
                            <ShoppingCart className="size-4 text-muted-foreground opacity-40" />
                          )}
                        </div>

                        {/* Name + Usage */}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm leading-tight font-medium text-foreground">
                            {product.productName}
                          </p>
                          <p className="text-xs leading-tight text-muted-foreground">
                            {t('MARKETING_EMAIL.USED_TIMES')} {product.usageCount}x
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Email Preview */}
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-muted/40">
            {/* Settings Bar */}
            <div className="flex shrink-0 flex-wrap items-center gap-2 border-b bg-background px-3 py-2">
              {/* Layout */}
              <Popover
                open={Boolean(layoutAnchorEl)}
                onOpenChange={(open) => { if (!open) setLayoutAnchorEl(null); }}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={settingsBarButtonClass}
                    onClick={(e) => setLayoutAnchorEl(e.currentTarget)}
                  >
                    {getLayoutName(selectedProductLayout)}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="min-w-52 p-2">
                  <div className="flex flex-col gap-1">
                    {productLayouts.map((layout) => (
                      <button
                        key={layout.id}
                        type="button"
                        onClick={() => {
                          setSelectedProductLayout(layout.id);
                          setLayoutAnchorEl(null);
                        }}
                        className={cn(
                          "border px-3 py-2 text-start text-sm transition-colors",
                          selectedProductLayout === layout.id
                            ? "border-foreground bg-muted font-medium text-foreground"
                            : "border-transparent text-muted-foreground hover:border-border hover:bg-muted"
                        )}
                      >
                        {getLayoutName(layout.id)}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Template */}
              <Popover
                open={Boolean(templateAnchorEl)}
                onOpenChange={(open) => { if (!open) setTemplateAnchorEl(null); }}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(settingsBarButtonClass, !selectedTemplate && "text-muted-foreground")}
                    onClick={(e) => setTemplateAnchorEl(e.currentTarget)}
                  >
                    {selectedTemplate ? selectedTemplate.name : t('MARKETING_EMAIL.SELECT_TEMPLATE')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="min-w-52 p-2">
                  <div className="flex flex-col gap-1">
                    {templates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => {
                          setSelectedTemplate(template);
                          setTemplateAnchorEl(null);
                        }}
                        className={cn(
                          "border px-3 py-2 text-start text-sm transition-colors",
                          selectedTemplate?.id === template.id
                            ? "border-foreground bg-muted font-medium text-foreground"
                            : "border-transparent text-muted-foreground hover:border-border hover:bg-muted"
                        )}
                      >
                        {template.name}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Discount */}
              <Popover
                open={voucherOpen}
                onOpenChange={(open) => { if (!open) handleVoucherClose(); }}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={settingsBarButtonClass}
                    onClick={handleVoucherClick}
                  >
                    <Tag data-icon="inline-start" />
                    {customization.voucherCode || t('MARKETING_EMAIL.ADD_DISCOUNT')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-[300px] p-4">
                  <p className="mb-3 text-sm font-medium text-foreground">
                    {t('MARKETING_EMAIL.VOUCHER_CONFIGURATION')}
                  </p>

                  <div className="flex flex-col gap-3">
                    <div>
                      <Label htmlFor="marketing-voucher-code" className="mb-1 text-xs text-muted-foreground">
                        {t('MARKETING_EMAIL.VOUCHER_CODE')}
                      </Label>
                      <Input
                        id="marketing-voucher-code"
                        value={customization.voucherCode}
                        onChange={(e) => setCustomization(prev => ({ ...prev, voucherCode: e.target.value }))}
                        placeholder="e.g., SAVE20"
                      />
                    </div>

                    <div>
                      <Label htmlFor="marketing-voucher-value" className="mb-1 text-xs text-muted-foreground">
                        {t('MARKETING_EMAIL.VOUCHER_VALUE')}
                      </Label>
                      <Input
                        id="marketing-voucher-value"
                        value={customization.voucherValue}
                        onChange={(e) => setCustomization(prev => ({ ...prev, voucherValue: e.target.value }))}
                        placeholder="e.g., 10%, $50"
                      />
                    </div>

                    <div>
                      <Label className="mb-1 text-xs text-muted-foreground">
                        {t('MARKETING_EMAIL.EXPIRY_DATE')}
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="h-9 w-full justify-start">
                            <CalendarDays data-icon="inline-start" />
                            {dayjs(customization.voucherExpiryDate).format('MMM D, YYYY')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={dayjs(customization.voucherExpiryDate).toDate()}
                            onSelect={(newValue) => {
                              if (newValue) {
                                setCustomization(prev => ({
                                  ...prev,
                                  voucherExpiryDate: dayjs(newValue).toISOString()
                                }));
                              }
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <p className="pt-1 text-xs font-medium">
                      {isVoucherEnabled ? (
                        <span className="text-success">{t('MARKETING_EMAIL.VOUCHER_WILL_BE_INCLUDED')}</span>
                      ) : (
                        <span className="text-warning">{t('MARKETING_EMAIL.FILL_REQUIRED_FIELDS')}</span>
                      )}
                    </p>
                  </div>
                </PopoverContent>
              </Popover>

              <div className="flex-1" />

              {/* Clear All + Separator (only if something is filled) */}
              {hasDraftContent && (
                <>
                  <Button
                    variant="ghost"
                    className={cn(settingsBarButtonClass, "text-muted-foreground hover:bg-transparent hover:text-destructive")}
                    onClick={handleClearAll}
                  >
                    {t('MARKETING_EMAIL.CLEAR_ALL')}
                  </Button>

                  <div className="h-5 w-px bg-border" />
                </>
              )}

              {/* Generate with AI */}
              <Button
                variant="outline"
                className={settingsBarButtonClass}
                onClick={handleGenerateWithAI}
                disabled={isGeneratingAI}
              >
                {isGeneratingAI
                  ? <Loader2 className="size-3.5 animate-spin" data-icon="inline-start" />
                  : <span data-icon="inline-start"><SparkleIcon /></span>}
                {isGeneratingAI ? t('MARKETING_EMAIL.GENERATING') : t('MARKETING_EMAIL.GENERATE_WITH_AI')}
              </Button>

              {/* Settings */}
              <Button
                variant="outline"
                className={settingsBarButtonClass}
                onClick={() => navigate('/marketing/email/settings?tab=templates')}
              >
                <Settings data-icon="inline-start" />
                {t('SIDEBAR.MARKETING.EMAIL_SETTINGS')}
              </Button>
            </div>

            {/* Preview iframe */}
            <div className="m-3 flex-1 overflow-hidden border bg-background">
              <iframe
                srcDoc={generateEmailPreview()}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                title="Email Preview"
              />
            </div>

            {/* What the preview can't show: the footer is appended on send. */}
            {postalAddressStatus === 'ready' && (
              <div className="mx-3 mb-3">
                {postalAddress ? (
                  <Alert>
                    <AlertDescription>
                      {t('MARKETING_EMAIL.FOOTER_ADDED_ON_SEND')}
                      <span className="mt-1 block">{postalAddress}</span>
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert className="border-warning/50 [&_svg]:text-warning">
                    <TriangleAlert />
                    <AlertDescription>
                      {/* The link is interpolated into the sentence rather than sitting
                          in an action slot, so the copy can say WHERE the setting
                          lives — and each language can place it naturally. Note the
                          target is the SMTP sub-route: the bare settings path opens the
                          Templates tab, which is not where the field is. */}
                      <Trans
                        i18nKey="MARKETING_EMAIL.NO_POSTAL_ADDRESS"
                        components={{
                          link: (
                            <button
                              type="button"
                              onClick={() => navigate('/marketing/email/settings?tab=templates&tab=smtp')}
                              className="font-medium text-foreground underline underline-offset-2"
                            />
                          ),
                        }}
                      />
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex shrink-0 justify-end border-t bg-background px-4 pt-3 pb-2">
          <Button
            variant="outline"
            className="h-9"
            disabled={!selectedTemplate || selectedProducts.length === 0 || !subject.trim() || !leadEmail || isCheckingSmtp || missingPostalAddress}
            onClick={async () => {
              if (isCheckingSmtp) return;
              setIsCheckingSmtp(true);
              try {
                const config = await settingsApi.findConfig();
                const smtp = config?.smtp;
                if (!smtp || !smtp.host || !smtp.user || smtp.pass === '') {
                  setSmtpError(true);
                  setTimeout(() => navigate('/marketing/email/settings?tab=templates&tab=smtp'), 2500);
                  return;
                }
                const result = await testSmtpConnection({
                  host: smtp.host,
                  port: smtp.port,
                  user: smtp.user,
                  pass: smtp.pass,
                });
                if (!result.connected) {
                  setSmtpError(true);
                  setTimeout(() => navigate('/marketing/email/settings?tab=templates&tab=smtp'), 2500);
                  return;
                }
                setConfirmSendOpen(true);
              } catch {
                setSmtpError(true);
                setTimeout(() => navigate('/marketing/email/settings?tab=templates&tab=smtp'), 2500);
              } finally {
                setIsCheckingSmtp(false);
              }
            }}
          >
            {isCheckingSmtp
              ? <Loader2 className="size-4 animate-spin" data-icon="inline-start" />
              : <Send data-icon="inline-start" />}
            {isCheckingSmtp ? t('MARKETING_EMAIL.CHECKING') : t('MARKETING_EMAIL.SEND_EMAIL_CAMPAIGN')}
          </Button>
        </div>

        {/* Send Confirmation Dialog */}
        <Dialog open={confirmSendOpen} onOpenChange={(open) => { if (!open) setConfirmSendOpen(false); }}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">
                {t('MARKETING_EMAIL.CONFIRM_EMAIL_CAMPAIGN')}
              </DialogTitle>
            </DialogHeader>

            <div className="px-4 pb-2">
              {/* Recipient Info */}
              <div className="mb-4 border bg-muted/50 p-3">
                <p className="mb-1 text-xs font-medium text-muted-foreground">
                  {t('MARKETING_EMAIL.ABOUT_TO_SEND_EMAIL')}
                </p>
                <p className="text-sm font-medium text-foreground">{leadEmail}</p>
              </div>

              {/* Campaign Details */}
              <div className="border bg-background">
                {/* Section Header */}
                <div className="border-b bg-muted/50 px-3 py-2">
                  <p className="text-sm font-medium text-foreground">
                    {t('MARKETING_EMAIL.CAMPAIGN_SUMMARY')}
                  </p>
                </div>

                {/* Details List */}
                <div className="flex flex-col gap-2.5 p-3">
                  <SummaryRow label={t('MARKETING_EMAIL.SUBJECT')}>{subject}</SummaryRow>
                  <SummaryRow label={t('MARKETING_EMAIL.TEMPLATE')}>{selectedTemplate?.name}</SummaryRow>
                  <SummaryRow label="Layout">{getLayoutName(selectedProductLayout)}</SummaryRow>
                  <SummaryRow label={t('MARKETING_EMAIL.PRODUCTS')}>
                    {getFilteredProductsForLayout().length} {t('MARKETING_EMAIL.FEATURED')}
                    {getFilteredProductsForLayout().length < selectedProducts.length && (
                      <span className="ms-1 text-muted-foreground">
                        ({selectedProducts.length} {t('MARKETING_EMAIL.SELECTED')})
                      </span>
                    )}
                  </SummaryRow>
                  <SummaryRow label={t('MARKETING_EMAIL.VOUCHER')}>
                    {isVoucherEnabled ? `${customization.voucherCode} (${customization.voucherValue})` : t('MARKETING_EMAIL.NONE')}
                  </SummaryRow>
                </div>
              </div>

              {sendError && (
                <Alert variant="destructive" className="mt-4">
                  <TriangleAlert />
                  <AlertDescription>{sendError}</AlertDescription>
                  <AlertAction>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label={t('MARKETING_EMAIL.CANCEL') as string}
                      onClick={() => setSendError(null)}
                    >
                      <X />
                    </Button>
                  </AlertAction>
                </Alert>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmSendOpen(false)}>
                {t('MARKETING_EMAIL.CANCEL')}
              </Button>
              <Button
                variant="outline"
                disabled={isSendingEmail}
                onClick={handleSendEmail}
              >
                {isSendingEmail
                  ? <Loader2 className="size-4 animate-spin" data-icon="inline-start" />
                  : <Send data-icon="inline-start" />}
                {isSendingEmail ? t('MARKETING_EMAIL.SENDING_CAMPAIGN') : t('MARKETING_EMAIL.SEND_CAMPAIGN')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Email Sent Success Dialog */}
        <Dialog
          open={emailSentSuccess}
          onOpenChange={(open) => {
            if (!open) {
              setEmailSentSuccess(false);
              navigate('/visualizer/engagement/leads');
            }
          }}
        >
          <DialogContent className="sm:max-w-lg">
            {/* Radix needs a name for the dialog; this one shows an animation and
                its own message, so the name is announced rather than drawn. */}
            <DialogTitle className="sr-only">
              {t('MARKETING_EMAIL.EMAIL_SENT_SUCCESSFULLY')}
            </DialogTitle>
            <div className="relative flex min-h-[400px] flex-col items-center justify-center p-8">
              {/* Large Lottie Animation */}
              <div className="mb-4 flex h-[300px] w-full max-w-[300px] items-center justify-center">
                <Lottie
                  animationData={sendMessageAnimation}
                  loop={false}
                  style={{ width: '100%', height: '100%' }}
                />
              </div>

              {/* Success Message */}
              <div className="animate-in fade-in text-center delay-1000 duration-700">
                <p className="mb-1.5 text-lg font-medium tracking-tight text-foreground">
                  {t('MARKETING_EMAIL.EMAIL_SENT_SUCCESSFULLY')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('MARKETING_EMAIL.CAMPAIGN_DELIVERED')} <span className="font-medium text-foreground">{leadEmail}</span>
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* SMTP Error Toast */}
      {smtpError && (
        <div
          role="alert"
          className="animate-in fade-in slide-in-from-bottom-4 fixed inset-x-4 bottom-6 z-50 mx-auto flex w-fit max-w-[calc(100%-2rem)] items-center gap-3 border border-warning/40 bg-warning/10 px-3 py-2 text-sm shadow-sm"
        >
          <span>{t('MARKETING_EMAIL.SMTP_NOT_CONFIGURED')}</span>
          <button
            type="button"
            onClick={() => setSmtpError(false)}
            aria-label={t('MARKETING_EMAIL.CANCEL') as string}
            className="text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </div>
      )}
    </>
  );
};
